import { useMutation } from "@tanstack/react-query"
import { useState } from "react"

import signUpApi from "../api/signUpApi"
import type { EstimateDraft } from "../types/SignUpEstimate"

type UseSignupFormOptions = {
  onSuccess?: (email: string) => void
  estimateDraft?: EstimateDraft
  coupon?: string
  url?: URL | null
}

const useSignupForm = (options: UseSignupFormOptions = {}) => {
  const { onSuccess, estimateDraft, coupon, url } = options

  const [email, setEmail] = useState("")
  const [agreedToTerms, setAgreedToTerms] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")

  const onEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value)
  }

  const onAgreedToTermsChange = (checked: boolean | "indeterminate") => {
    setAgreedToTerms(checked === true)
  }

  const { mutate, isLoading } = useMutation(
    () =>
      signUpApi.apply({
        email,
        coupon: coupon ?? undefined,
        url: url ? encodeURIComponent(`${url.pathname}${url.search}${url.hash}`) : undefined,
        estimateDraft,
      }),
    {
      onMutate() {
        setErrorMessage("")
      },
      onSuccess(result) {
        if (result.isError) {
          const errorMsg = result.data.message || "エラーが発生しました。もう一度お試しください。"
          setErrorMessage(errorMsg)
          return
        }
        onSuccess?.(result.data.acceptedEmail)
      },
      onError(error) {
        const errorMessage = error instanceof Error ? error.message : "システムエラーが発生しました。しばらくしてからもう一度お試しください。"
        setErrorMessage(errorMessage)
      },
    },
  )

  const submit = () => {
    if (!email) {
      setErrorMessage("メールアドレスを入力してください")
      return
    }
    if (!agreedToTerms) {
      setErrorMessage("利用規約に同意してください")
      return
    }
    mutate()
  }

  return {
    email,
    agreedToTerms,
    onEmailChange,
    onAgreedToTermsChange,
    errorMessage,
    submit,
    isLoading,
  }
}

export default useSignupForm
